import { useState, useEffect, useRef } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Input } from '../ui/Input'
import { Badge } from '../ui/Badge'
import { DiscountModal } from './DiscountModal'
import { PriceOverrideModal } from './PriceOverrideModal'
import { ShoppingCart, Trash2, Minus, Plus, Package, Percent, Edit } from 'lucide-react'

interface CartItem {
  id: string
  name: string
  originalPrice: number
  price: number
  quantity: number
  total: number
  gst_rate: number
  gst_type: 'inclusive' | 'exclusive'
  discount?: {
    type: 'fixed' | 'percentage'
    value: number
    amount: number
  }
  isWeighable?: boolean
  unit?: string
}

interface CartTableProps {
  cart: CartItem[]
  onUpdateQuantity: (productId: string, quantity: number) => void
  onRemoveFromCart: (productId: string) => void
  onClearCart: () => void
  formatCurrency: (amount: number) => string
  highlightedItems?: Set<string>
  onApplyItemDiscount?: (itemId: string, type: 'fixed' | 'percentage', value: number) => void
  onRemoveItemDiscount?: (itemId: string) => void
  onOverridePrice?: (itemId: string, price: number) => void
}

export const CartTable = ({ 
  cart, 
  onUpdateQuantity, 
  onRemoveFromCart, 
  onClearCart, 
  formatCurrency,
  highlightedItems = new Set(),
  onApplyItemDiscount,
  onRemoveItemDiscount,
  onOverridePrice
}: CartTableProps) => {
  const [showItemDiscountModal, setShowItemDiscountModal] = useState<string | null>(null)
  const [showPriceOverrideModal, setShowPriceOverrideModal] = useState<string | null>(null)
  
  // Refs for auto-scrolling
  const tableScrollContainerRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({})
  
  // Auto-scroll to highlighted items
  useEffect(() => {
    if (highlightedItems.size > 0 && tableScrollContainerRef.current) {
      // Get the first highlighted item
      const highlightedItemId = Array.from(highlightedItems)[0]
      const rowElement = rowRefs.current[highlightedItemId]
      
      if (rowElement) {
        // Smooth scroll to the highlighted row with some padding
        rowElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }
    }
  }, [highlightedItems])
  
  const currentItemForDiscount = showItemDiscountModal ? cart.find(item => item.id === showItemDiscountModal) : null
  const currentItemForPriceOverride = showPriceOverrideModal ? cart.find(item => item.id === showPriceOverrideModal) : null
  if (cart.length === 0) {
    return (
      <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Cart Items (0)
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">Cart is Empty</h3>
            <p className="text-gray-600">Search and add products to get started</p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card className="flex-1 flex flex-col overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Cart Items ({cart.length})
          </h3>
          <Button variant="outline" size="sm" onClick={onClearCart}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Cart
          </Button>
        </div>
      </div>

      <div ref={tableScrollContainerRef} className="flex-1 overflow-auto">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Price</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">GST</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Quantity</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {cart.map((item) => {
              const isHighlighted = highlightedItems.has(item.id)
              return (
              <tr 
                key={item.id}
                ref={(el) => { rowRefs.current[item.id] = el }}
                className={`transition-all duration-500 ${
                  isHighlighted 
                    ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-600' 
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <td className="px-4 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{item.name}</span>
                      {item.isWeighable && (
                        <Badge variant="secondary" size="sm" className="text-xs">
                          {item.unit}
                        </Badge>
                      )}
                      {item.price !== item.originalPrice && (
                        <Badge variant="warning" size="sm" className="text-xs">
                          Override
                        </Badge>
                      )}
                      {item.discount && (
                        <Badge variant="success" size="sm" className="text-xs">
                          {item.discount.type === 'percentage' ? `${item.discount.value}%` : `₹${item.discount.value}`}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Original: {formatCurrency(item.originalPrice)} - GST: {item.gst_rate}% {item.gst_type === 'inclusive' ? 'inc' : 'exc'}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    {formatCurrency(item.price)}
                  </div>
                  {item.discount && (
                    <div className="text-xs text-green-600 dark:text-green-400">
                      -{formatCurrency(item.discount.amount)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-4 text-center">
                  <div className="text-xs">
                    <div className="font-medium text-gray-700 dark:text-gray-300">{item.gst_rate}%</div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {formatCurrency(((item.total * item.gst_rate) / 100))}
                    </div>
                    <div className="text-gray-400 dark:text-gray-500">incl.</div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const decrement = item.isWeighable ? 0.1 : 1
                        const minQty = item.isWeighable ? 0.1 : 1
                        onUpdateQuantity(item.id, Math.max(minQty, item.quantity - decrement))
                      }}
                      className="w-7 h-7 p-0"
                      title="Decrease quantity"
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Input
                      type="number"
                      min={item.isWeighable ? "0.01" : "1"}
                      step={item.isWeighable ? "0.1" : "1"}
                      value={item.quantity}
                      onChange={(e) => {
                        const qty = parseFloat(e.target.value) || (item.isWeighable ? 0.1 : 1)
                        const minQty = item.isWeighable ? 0.01 : 1
                        onUpdateQuantity(item.id, Math.max(minQty, qty))
                      }}
                      className="w-16 text-center h-7 text-sm px-1"
                      title="Quantity"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const increment = item.isWeighable ? 0.1 : 1
                        onUpdateQuantity(item.id, item.quantity + increment)
                      }}
                      className="w-7 h-7 p-0"
                      title="Increase quantity"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
                <td className="px-4 py-4 text-right font-medium text-gray-900 dark:text-gray-100 text-sm">
                  {formatCurrency(item.total)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center gap-1">
                    {/* Discount Button */}
                    {item.discount ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemoveItemDiscount?.(item.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 w-7 h-7 p-0"
                        title="Remove Discount"
                      >
                        <Percent className="h-3 w-3" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowItemDiscountModal(item.id)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 w-7 h-7 p-0"
                        title="Apply Discount"
                      >
                        <Percent className="h-3 w-3" />
                      </Button>
                    )}
                    
                    {/* Price Override Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPriceOverrideModal(item.id)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 w-7 h-7 p-0"
                      title="Override Price"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    
                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveFromCart(item.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 w-7 h-7 p-0"
                      title="Remove item from cart"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
              </tr>
              )
            })}
          </tbody>
          </table>
        </div>
      </div>
      
      {/* Modals */}
      <DiscountModal
        isOpen={showItemDiscountModal !== null}
        onClose={() => setShowItemDiscountModal(null)}
        onApply={(type, value) => {
          if (showItemDiscountModal && onApplyItemDiscount) {
            onApplyItemDiscount(showItemDiscountModal, type, value)
          }
          setShowItemDiscountModal(null)
        }}
        title={`Apply Discount - ${currentItemForDiscount?.name || ''}`}
        currentPrice={currentItemForDiscount ? currentItemForDiscount.price * currentItemForDiscount.quantity : undefined}
      />
      
      <PriceOverrideModal
        isOpen={showPriceOverrideModal !== null}
        onClose={() => setShowPriceOverrideModal(null)}
        onApply={(newPrice) => {
          if (showPriceOverrideModal && onOverridePrice) {
            onOverridePrice(showPriceOverrideModal, newPrice)
          }
          setShowPriceOverrideModal(null)
        }}
        itemName={currentItemForPriceOverride?.name || ''}
        originalPrice={currentItemForPriceOverride?.originalPrice || 0}
        currentPrice={currentItemForPriceOverride?.price || 0}
      />
    </Card>
  )
}